import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
// import Colors from '../../utils/colors.json'
import {
  getAgeBand,
  getPriceList,
  getProducts,
} from '../../services/PremiumService'

const styles = () => ({
  card: {
    backgroundColor: '#67BBB3',
    padding: '0.9rem',
    // borderRadius: '8px',
  },
  cardContent: {
    background: '#FFFFFF',
    borderRadius: '8px',
  },
  cardheaderText: {
    fontSize: '17px',
    color: '#FFFFFF',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  premiumBtn: {
    background: '#FF8C00',
    borderRadius: '20px',
    textTransform: 'capitalize',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  formTextField: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#2F2F2F',
    height: 40,
    borderRadius: '20px',
  },
  formLabel: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '14.4px',
    color: '#333333',
    marginBottom: '4px',
  },
})

const PremiumCalculator = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const [products, setProducts] = useState([])
  const [ageBands, setAgeBands] = useState([])
  const [discounts, setDiscounts] = useState([])
  const [premium, setPremium] = useState(0)
  const [formData, setFormData] = useState({
    numberOfAdults: 0,
    numberOfChildren: 0,
    totalFamilyMembers: 0,
    maxAge: '',
    planType: null,
    discount: '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const getPremiumProducts = useCallback(async () => {
    const response = await getProducts()
    setProducts(response?.data)
  }, [])

  const getPremiumAgeBand = useCallback(async () => {
    const response = await getAgeBand()
    setAgeBands(response?.data?.age_band)
    setDiscounts(response?.data?.type)
  }, [])

  const getPremiumPriceList = useCallback(async () => {
    const response = await getPriceList(
      formData?.planType,
      formData?.maxAge,
      formData?.discount,
      formData?.numberOfAdults,
      formData?.numberOfChildren
    )
    setPremium(response?.data?.price)
  }, [
    formData?.planType,
    formData?.maxAge,
    formData?.discount,
    formData?.numberOfAdults,
    formData?.numberOfChildren,
  ])

  useEffect(() => {
    getPremiumProducts()
    getPremiumAgeBand()
  }, [getPremiumProducts, getPremiumAgeBand])

  return (
    <Card className={classes?.card}>
      <CardHeader
        title={
          <Box className={classes?.cardheaderText}>Premium Calculator</Box>
        }
      />
      <CardContent className={classes?.cardContent}>
        <div className={classes?.formRow}>
          {/* <FormControl fullWidth>
            <Typography className={classes?.formLabel}>Product</Typography>
            <TextField
              select
              name='product'
              placeholder='Select product'
              size='small'
              slotProps={{
                input: {
                  className: classes?.formTextField,
                },
              }}
              value={formData?.product}
              onChange={handleChange}
            >
              {products?.map((item: { id: number; name: string }, index) => (
                <MenuItem key={index} value={item?.id}>
                  {item?.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl> */}

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>
              Number of Adults
            </Typography>
            <TextField
              data-testid='numberOfAdults-select'
              select
              name='numberOfAdults'
              placeholder='Select number of adults'
              size='small'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.numberOfAdults}
              onChange={handleChange}
            >
              <MenuItem value={0} disabled>
                0
              </MenuItem>
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>
              Number of Children
            </Typography>
            <TextField
              data-testid='numberOfChildren-select'
              select
              name='numberOfChildren'
              placeholder='Select number of children'
              size='small'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.numberOfChildren}
              onChange={handleChange}
            >
              {Array.from({ length: 10 }, (_, index) => (
                <MenuItem key={index} value={index}>
                  {index}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>
              Total Number of Family Members
            </Typography>
            <TextField
              data-testid='totalFamilyMembers-select'
              disabled
              name='totalFamilyMembers'
              size='small'
              type='number'
              fullWidth
              //   placeholder='Total Number of Family Members'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.numberOfAdults + formData?.numberOfChildren}
              //   onChange={handleChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>Max Age</Typography>
            <TextField
              data-testid='maxAge-select'
              name='maxAge'
              select
              placeholder='Select max age'
              size='small'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.maxAge}
              onChange={handleChange}
            >
              {ageBands?.map((item: { name: string; value: string }, index) => (
                <MenuItem key={index} value={item?.value}>
                  {item?.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>Plan Type</Typography>
            <TextField
              data-testid='plan-select'
              name='planType'
              select
              placeholder='Select plan type'
              size='small'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.planType || ''}
              onChange={handleChange}
            >
              {products?.map((item: { id: number; name: string }, index) => (
                <MenuItem key={index} value={item?.id}>
                  {item?.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          <FormControl fullWidth>
            <Typography className={classes?.formLabel}>Discount</Typography>
            <TextField
              data-testid='discount-select'
              name='discount'
              select
              placeholder='Select discount'
              size='small'
              InputProps={{
                className: classes?.formTextField,
              }}
              value={formData?.discount}
              onChange={handleChange}
            >
              {discounts?.map(
                (item: { name: number; value: string }, index) => (
                  <MenuItem key={index} value={item?.value}>
                    {item?.name}
                  </MenuItem>
                )
              )}
            </TextField>
          </FormControl>
        </div>
        {premium > 0 && (
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            gap={1}
            mt={2}
            mb={2}
          >
            <Typography component={'span'} fontSize={'17px'} fontWeight={600}>
              Premimum
            </Typography>
            -
            <Typography component={'span'} fontSize={'17px'} fontWeight={400}>
              ₹ {premium}
            </Typography>
          </Box>
        )}
        <Box display={'flex'} justifyContent={'center'}>
          <Button
            variant='contained'
            className={classes?.premiumBtn}
            onClick={getPremiumPriceList}
          >
            Calculate Premium
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}

export default PremiumCalculator
